import {
  AccessTime,
  Inventory,
  LocalGasStation,
  Speed,
  TrendingUp,
} from '@mui/icons-material';

import { FUEL_USAGE_METRICS_MAP, TRAVEL_TIME_METRICS_MAP } from './constants';
import {
  type DispatchPlanTripsResponseType,
  type DispatchPlanFuelUsageMetricsResponseType,
  type DispatchPlanMaterialDumpResponseType,
  type DispatchPlanTravelTimeMetricsResponseType,
  type LoadingDumpingDurationsResponseType,
  type PlanComparisonKpi,
  type PlanDataType,
  type TravelStatsResponseType,
} from '../CreateDispatchPlan/Steps/types/steps.types';
import { type GeoTrip } from 'types/geo.types';
import {
  convertBasedOnUnitSystem,
  convertLitersToGallons,
} from 'utils/helpers/unitSystem';

interface MetricsType {
  fuel_usage: DispatchPlanFuelUsageMetricsResponseType;
  material_dumped: DispatchPlanMaterialDumpResponseType;
  travel_time: DispatchPlanTravelTimeMetricsResponseType;
  trip: DispatchPlanTripsResponseType;
}

export const normalizeSiteKey = (location?: string): 'L0' | 'L1' | 'OTHER' => {
  const value = (location ?? '').toUpperCase().trim();
  if (/\bL0\b/.test(value) || /\bSITE\s*0\b/.test(value)) return 'L0';
  if (/\bL1\b/.test(value) || /\bSITE\s*1\b/.test(value)) return 'L1';
  return 'OTHER';
};

/**
 * Collapse consecutive traveling segments that are intermediate intersection
 * hops (I*) into a single logical L→D or D→L traveling record for display.
 */
export const collapseTravelSegments = (
  planData: PlanDataType[]
): PlanDataType[] => {
  const result: PlanDataType[] = [];
  let i = 0;
  while (i < planData.length) {
    const row = planData[i];
    if (row.action !== 'traveling') {
      result.push(row);
      i++;
      continue;
    }
    // collect consecutive traveling rows with the same loading_area+dumping_area
    const groupKey = `${row.loading_area ?? ''}-${row.dumping_area ?? ''}`;
    let j = i + 1;
    while (
      j < planData.length &&
      planData[j].action === 'traveling' &&
      `${planData[j].loading_area ?? ''}-${planData[j].dumping_area ?? ''}` ===
        groupKey
    ) {
      j++;
    }
    if (j === i + 1) {
      result.push(row);
    } else {
      const group = planData.slice(i, j);
      const merged: PlanDataType = {
        ...row,
        end_location: group[group.length - 1].end_location,
        end_ts: group[group.length - 1].end_ts,
        travel_time: group.reduce(
          (s, r) => s + (Number(r.travel_time) || 0),
          0
        ),
        fuel_usage: group.reduce((s, r) => s + (Number(r.fuel_usage) || 0), 0),
      };
      result.push(merged);
    }
    i = j;
  }
  return result;
};

/**
 * Compute metrics for one bumper number's records.
 */
export const computeMetricsForBumperNumber = (
  records: PlanDataType[],
  bumperNumber: string
): MetricsType => {
  // initial vals
  const fuelUsage = {
    moving_loaded_fuel: 0,
    moving_unloaded_fuel: 0,
    idling_fuel: 0,
    loading_fuel: 0,
    dumping_fuel: 0,
  };

  let materialDumped = 0;
  const travelTime = {
    moving_loaded_mins: 0,
    moving_unloaded_mins: 0,
    idling_mins: 0,
    loading_mins: 0,
    dumping_mins: 0,
  };
  let tripCount = 0;

  for (const record of records) {
    const action = record.action?.toLowerCase() || '';
    const fuel = Number(record.fuel_usage) || 0;
    const time = Number(record.travel_time) || 0;
    const dumped = Number(record.material_dumped) || 0;

    let type: string | null = null;
    if (action === 'loading') {
      type = 'loading';
    } else if (action === 'dumping') {
      type = 'dumping';
    } else if (action === 'idling') {
      type = 'idling';
    } else if (action === 'traveling') {
      type = dumped > 0 ? 'moving_loaded' : 'moving_unloaded';
    }

    if (type) {
      const fuelKey = `${type}_fuel` as keyof typeof fuelUsage;
      const timeKey = `${type}_mins` as keyof typeof travelTime;
      if (fuelUsage[fuelKey] !== undefined) fuelUsage[fuelKey] += fuel;
      if (travelTime[timeKey] !== undefined) travelTime[timeKey] += time;
    }

    if (action === 'dumping') {
      tripCount += 1;
    }
    // API plan data puts material_dumped on loading rows; master algo puts it on dumping rows.
    // Only one of the two ever has a non-zero value per trip, so summing all rows is safe.
    materialDumped += dumped;
  }

  return {
    fuel_usage: { ...fuelUsage, bumper_number: bumperNumber },
    material_dumped: { total: materialDumped, bumper_number: bumperNumber },
    travel_time: { ...travelTime, bumper_number: bumperNumber },
    trip: { total: tripCount, bumper_number: bumperNumber },
  };
};

export const aggregateSchedulePlanByBumperNumber = (
  planData: PlanDataType[]
): {
  groupedPlanData: Record<string, PlanDataType[]>;
  groupedMetrics: {
    fuel_usage: Record<string, DispatchPlanFuelUsageMetricsResponseType>;
    material_dumped: Record<string, DispatchPlanMaterialDumpResponseType>;
    travel_time: Record<string, DispatchPlanTravelTimeMetricsResponseType>;
    trip: Record<string, DispatchPlanTripsResponseType>;
  };
} => {
  try {
    if (!Array.isArray(planData) || planData.length === 0) {
      throw new Error('Invalid data type for plan');
    }

    const groupedPlanData = planData.reduce<Record<string, PlanDataType[]>>(
      (acc, obj) => {
        if (!obj || typeof obj !== 'object') {
          return acc;
        }

        const bumper = obj.bumper_number;
        if (!bumper) {
          console.warn('No bumper_number found', obj);
          return acc;
        }

        if (!acc[bumper]) {
          acc[bumper] = [];
        }
        acc[bumper].push(obj);

        return acc;
      },
      {}
    );

    const groupedMetrics: {
      fuel_usage: Record<string, DispatchPlanFuelUsageMetricsResponseType>;
      material_dumped: Record<string, DispatchPlanMaterialDumpResponseType>;
      travel_time: Record<string, DispatchPlanTravelTimeMetricsResponseType>;
      trip: Record<string, DispatchPlanTripsResponseType>;
    } = {
      fuel_usage: {},
      material_dumped: {},
      travel_time: {},
      trip: {},
    };

    for (const [bumperNumber, records] of Object.entries(groupedPlanData)) {
      const metrics = computeMetricsForBumperNumber(records, bumperNumber);

      groupedMetrics.fuel_usage[bumperNumber] = metrics.fuel_usage;
      groupedMetrics.material_dumped[bumperNumber] = metrics.material_dumped;
      groupedMetrics.travel_time[bumperNumber] = metrics.travel_time;
      groupedMetrics.trip[bumperNumber] = metrics.trip;
    }

    return { groupedPlanData, groupedMetrics };
  } catch (error) {
    console.info('Error aggregating planData', error);
    return {
      groupedPlanData: {},
      groupedMetrics: {
        fuel_usage: {},
        material_dumped: {},
        travel_time: {},
        trip: {},
      },
    };
  }
};

export const formatLocation = (location?: string) => {
  if (!location) return '';
  return location.toLowerCase() === 'd' ? 'Dumping' : `Site ${location}`;
};

export const formatTripsStatsForRedux = (
  travelStats: TravelStatsResponseType,
  loadingDumpingDurations: LoadingDumpingDurationsResponseType
): {
  travelTimes: Array<{
    loadingSiteId: string;
    dumpingSiteId: string;
    loadedMins: number;
    emptyMins: number;
  }>;
  loadingDurations: Array<{ id: string; durationMins: number }>;
  dumpingDurations: Array<{ id: string; durationMins: number }>;
} => {
  const safeRound = (val: number) => Math.max(1, Math.round(val));

  return {
    travelTimes: [
      {
        loadingSiteId: 'L0',
        dumpingSiteId: 'D0',
        loadedMins: safeRound(travelStats.l0_d0_avg),
        emptyMins: safeRound(travelStats.d0_l0_avg),
      },
      {
        loadingSiteId: 'L1',
        dumpingSiteId: 'D0',
        loadedMins: safeRound(travelStats.l1_d0_avg),
        emptyMins: safeRound(travelStats.d0_l1_avg),
      },
    ],
    loadingDurations: [
      {
        id: 'L0',
        durationMins: safeRound(loadingDumpingDurations.l0_loading_avg),
      },
      {
        id: 'L1',
        durationMins: safeRound(loadingDumpingDurations.l1_loading_avg),
      },
    ],
    dumpingDurations: [
      {
        id: 'D0',
        durationMins: safeRound(loadingDumpingDurations.d0_dumping_avg),
      },
    ],
  };
};

export const safeNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Build KPI totals and breakdowns for travel, fuel, and material metrics,
 * with optional comparison deltas against another plan.
 */
const DIFF_THRESHOLD = 0.1; // ignore changes smaller than 0.1
const buildBreakdown = <T extends Record<string, any>>(
  data: T[] | undefined,
  keys: Record<string, keyof T>,
  precision: number
) => {
  const breakdown: Record<string, string> = {};

  for (const [label, field] of Object.entries(keys)) {
    const subtotal =
      data?.reduce((sum, row) => sum + safeNumber(row[field]), 0) ?? 0;
    breakdown[label] = subtotal.toFixed(precision);
  }

  const total = Object.values(breakdown).reduce(
    (sum, val) => sum + Number(val),
    0
  );

  return { total, breakdown };
};

export const getPlanKpisWithComparison = (
  metrics: {
    travel_time: DispatchPlanTravelTimeMetricsResponseType[];
    fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
    material_dumped: DispatchPlanMaterialDumpResponseType[];
  },
  unitSystem: string,
  compareMetrics?: {
    travel_time: DispatchPlanTravelTimeMetricsResponseType[];
    fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
    material_dumped: DispatchPlanMaterialDumpResponseType[];
  }
): PlanComparisonKpi[] => {
  // Travel
  const travel = buildBreakdown(
    metrics?.travel_time,
    TRAVEL_TIME_METRICS_MAP,
    0
  );

  // Fuel
  const fuel = buildBreakdown(metrics?.fuel_usage, FUEL_USAGE_METRICS_MAP, 4);
  const fuelBreakDownConverted: Record<string, string> = {};
  for (const key in fuel.breakdown) {
    fuelBreakDownConverted[key] = `${convertBasedOnUnitSystem(
      convertLitersToGallons,
      fuel.breakdown[key],
      unitSystem,
      4
    )}`;
  }
  const fuelConverted = {
    total: convertBasedOnUnitSystem(
      convertLitersToGallons,
      fuel.total,
      unitSystem,
      4
    ),
    breakdown: fuelBreakDownConverted,
  };

  // Material
  const materialTotal =
    metrics?.material_dumped?.reduce(
      (sum, row) => sum + safeNumber(row.total),
      0
    ) ?? 0;
  const material: { total: number; breakdown: Record<string, string> } = {
    total: materialTotal,
    breakdown: {},
  };

  const fuelPerTon =
    material.total > 0
      ? convertBasedOnUnitSystem(
          convertLitersToGallons,
          fuel.total,
          unitSystem,
          4
        ) / material.total
      : 0;
  const productionRate =
    travel.total > 0 ? (material.total / travel.total) * 60 : 0;

  const kpis: PlanComparisonKpi[] = [
    {
      key: 'production_rate',
      icon: TrendingUp,
      label: 'Production Rate',
      value: productionRate.toFixed(2),
      unit: 'T/hr',
      showDelta: true,
      deltaColors: { up: 'success.main', down: 'error.main' },
    },
    {
      key: 'fuel_usage',
      icon: LocalGasStation,
      label: 'Fuel Usage',
      value: fuelConverted.total.toFixed(4),
      unit: unitSystem === 'metric' ? 'L' : 'Gals',
      breakdown: fuelConverted.breakdown,
      showDelta: true,
    },
    {
      key: 'fuel_per_ton',
      icon: Speed,
      label: 'Fuel per Ton',
      value: fuelPerTon.toFixed(4),
      unit: unitSystem === 'metric' ? 'L/T' : 'Gals/T',
      showDelta: true,
    },
    {
      key: 'travel_time',
      icon: AccessTime,
      label: 'Travel Time',
      value: travel.total.toFixed(0),
      unit: 'mins',
      breakdown: travel.breakdown,
      showDelta: true,
    },
    {
      key: 'tons_moved',
      icon: Inventory,
      label: 'Tons Moved',
      value: material.total.toFixed(1),
      unit: '',
      breakdown: material.breakdown,
      showDelta: true,
    },
  ];

  // Comparison
  if (compareMetrics) {
    const compareTravel = buildBreakdown(
      compareMetrics.travel_time,
      TRAVEL_TIME_METRICS_MAP,
      0
    );
    const compareFuel = buildBreakdown(
      compareMetrics.fuel_usage,
      FUEL_USAGE_METRICS_MAP,
      4
    );
    const compareMaterialTotal =
      compareMetrics.material_dumped?.reduce(
        (sum, row) => sum + safeNumber(row.total),
        0
      ) ?? 0;
    const compareMaterial: {
      total: number;
      breakdown: Record<string, string>;
    } = {
      total: compareMaterialTotal,
      breakdown: {},
    };

    const compareFuelPerTon =
      compareMaterial.total > 0
        ? convertBasedOnUnitSystem(
            convertLitersToGallons,
            compareFuel.total,
            unitSystem,
            2
          ) / compareMaterial.total
        : 0;

    const compareProductionRate =
      compareTravel.total > 0
        ? (compareMaterial.total / compareTravel.total) * 60
        : 0;

    const compareFuelTotal = convertBasedOnUnitSystem(
      convertLitersToGallons,
      compareFuel.total,
      unitSystem,
      4
    );

    const compareTotals: Record<string, number> = {
      travel_time: compareTravel.total,
      fuel_usage: compareFuelTotal,
      tons_moved: compareMaterial.total,
      fuel_per_ton: compareFuelPerTon,
      production_rate: compareProductionRate,
    };

    kpis.forEach((kpi) => {
      const diff = Number(kpi.value) - (compareTotals[kpi.key] ?? 0);

      if (Math.abs(diff) >= DIFF_THRESHOLD) {
        const precision = kpi.key === 'travel_time' ? 0 : 2;
        kpi.delta =
          diff > 0 ? `+${diff.toFixed(precision)}` : diff.toFixed(precision);
      }
    });
  }

  return kpis;
};

/**
 * Compute plan metrics (travel_time, fuel_usage, material_dumped, trips)
 * from planData. Used when the API doesn't return reliable metrics.
 */
export function computePlanMetrics(planData: PlanDataType[]): {
  travel_time: DispatchPlanTravelTimeMetricsResponseType[];
  fuel_usage: DispatchPlanFuelUsageMetricsResponseType[];
  material_dumped: DispatchPlanMaterialDumpResponseType[];
  trips: DispatchPlanTripsResponseType[];
} {
  if (!Array.isArray(planData) || planData.length === 0) {
    return { travel_time: [], fuel_usage: [], material_dumped: [], trips: [] };
  }

  const { groupedMetrics } = aggregateSchedulePlanByBumperNumber(
    collapseTravelSegments(planData)
  );

  return {
    travel_time: Object.values(groupedMetrics.travel_time),
    fuel_usage: Object.values(groupedMetrics.fuel_usage),
    material_dumped: Object.values(groupedMetrics.material_dumped),
    trips: Object.values(groupedMetrics.trip),
  };
}

/**
 * Convert a metrics array into an object keyed by bumper_number.
 * Preserves full type information for each metric entry.
 */
export function mapSchedulePlanMetricsByBumper<
  T extends { bumper_number: string }
>(metricsArray: T[] | null | undefined): Record<string, T> {
  try {
    if (!Array.isArray(metricsArray) || metricsArray.length === 0) {
      return {};
    }

    return metricsArray.reduce<Record<string, T>>((acc, metric) => {
      if (metric && typeof metric.bumper_number === 'string') {
        acc[metric.bumper_number] = metric;
      }
      return acc;
    }, {});
  } catch (error) {
    console.error('Error mapping schedule plan metrics by bumper:', error);
    return {};
  }
}

// this is to format actual plan data similar to tony algo
export const transformMasterAlgoToPlanDataByBumperNo = (
  tripsData: Record<string, GeoTrip[]>
): {
  planData: Record<string, PlanDataType[]>;
  travel_time: Record<string, DispatchPlanTravelTimeMetricsResponseType>;
  fuel_usage: Record<string, DispatchPlanFuelUsageMetricsResponseType>;
  material_dump: Record<string, DispatchPlanMaterialDumpResponseType>;
  trips_count: Record<string, DispatchPlanTripsResponseType>;
} => {
  const planData: Record<string, PlanDataType[]> = {};
  const travelTimeInMins: Record<
    string,
    DispatchPlanTravelTimeMetricsResponseType
  > = {};
  const fuelUsage: Record<string, DispatchPlanFuelUsageMetricsResponseType> =
    {};
  const materialDump: Record<string, DispatchPlanMaterialDumpResponseType> = {};
  const tripsCount: Record<string, DispatchPlanTripsResponseType> = {};

  Object.entries(tripsData).forEach(([bumperNumber, trips]) => {
    const planEntries: PlanDataType[] = [];

    // travel time in seconds
    const travelSeconds = {
      idling_sec: 0,
      loading_sec: 0,
      dumping_sec: 0,
      moving_loaded_sec: 0,
      moving_unloaded_sec: 0,
    };
    //  fuel usage
    const fuelTotals = {
      idling_fuel: 0,
      loading_fuel: 0,
      dumping_fuel: 0,
      moving_loaded_fuel: 0,
      moving_unloaded_fuel: 0,
    };

    // material and trips
    let totalMaterial = 0;
    let totalTrips = 0;

    trips.forEach((trip, index) => {
      const loadingLocation = normalizeSiteKey(
        trip?.loading_stop_area ?? trip?.loading_area ?? ''
      );
      const dumpingLocation = normalizeSiteKey(
        trip?.dumping_stop_area ?? trip?.unloading_area ?? ''
      );

      // tarvel times
      travelSeconds.idling_sec +=
        (Number(trip.short_idling_time) || 0) +
        (Number(trip.long_idling_time) || 0);
      travelSeconds.loading_sec += trip.loading_duration_sec || 0;
      travelSeconds.dumping_sec += trip.unloading_duration_sec || 0;
      travelSeconds.moving_loaded_sec += trip.loaded_trip_duration_sec || 0;
      travelSeconds.moving_unloaded_sec += trip.unloaded_trip_duration_sec || 0;

      // fuel usage
      fuelTotals.idling_fuel +=
        (trip.short_idling_fuel_consumed || 0) +
        (trip.long_idling_fuel_consumed || 0);
      fuelTotals.loading_fuel += trip.loading_fuel_consumption_l ?? 0;
      fuelTotals.dumping_fuel += trip.unloading_fuel_consumption_l ?? 0;
      fuelTotals.moving_loaded_fuel += trip.loaded_trip_fuel_consumption_l ?? 0;
      fuelTotals.moving_unloaded_fuel +=
        trip.unloaded_trip_fuel_consumption_l ?? 0;

      totalMaterial += trip.max_load_ton;
      totalTrips += 1;

      // create plan entries
      planEntries.push(
        {
          action: 'loading',
          bumper_number: bumperNumber,
          day: trip.process_date,
          device: trip.device,
          start_location: loadingLocation,
          end_location: loadingLocation,
          start_ts: trip.loading_start_ts,
          end_ts: trip.loading_end_ts,
          material_dumped: 0,
          travel_time: trip?.loading_duration_sec ?? 0,
          fuel_usage: trip?.loading_fuel_consumption_l ?? 0,
        },
        // from loading to dumping
        {
          action: 'travelling',
          bumper_number: bumperNumber,
          day: trip.process_date,
          device: trip.device,
          start_location: loadingLocation,
          end_location: dumpingLocation,
          start_ts: trip.loaded_trip_start_ts,
          end_ts: trip.loaded_trip_end_ts,
          material_dumped: 0,
          travel_time: trip?.loaded_trip_duration_sec ?? 0,
          fuel_usage: trip?.loaded_trip_fuel_consumption_l ?? 0,
        },
        {
          action: 'dumping',
          bumper_number: bumperNumber,
          day: trip.process_date,
          device: trip.device,
          start_location: dumpingLocation,
          end_location: dumpingLocation,
          start_ts: trip.unloading_start_ts,
          end_ts: trip.unloading_end_ts,
          material_dumped: trip.max_load_ton,
          travel_time: trip?.unloading_duration_sec ?? 0,
          fuel_usage: trip?.unloading_fuel_consumption_l ?? 0,
        },
        // from dumping to loading
        {
          action: 'traveling',
          bumper_number: bumperNumber,
          day: trip.process_date,
          device: trip.device,
          start_location: dumpingLocation,
          end_location: loadingLocation,
          start_ts: trip.unloaded_trip_start_ts,
          end_ts: trip.unloaded_trip_end_ts,
          material_dumped: 0, // unloaded, so no material carried
          travel_time: trip?.unloaded_trip_duration_sec ?? 0,
          fuel_usage: trip?.unloaded_trip_fuel_consumption_l ?? 0,
        }
      );
    });

    planData[bumperNumber] = planEntries;

    // convert seconds to minutes
    travelTimeInMins[bumperNumber] = {
      bumper_number: bumperNumber,
      idling_mins: Math.round(travelSeconds.idling_sec / 60),
      loading_mins: Math.round(travelSeconds.loading_sec / 60),
      dumping_mins: Math.round(travelSeconds.dumping_sec / 60),
      moving_loaded_mins: Math.round(travelSeconds.moving_loaded_sec / 60),
      moving_unloaded_mins: Math.round(travelSeconds.moving_unloaded_sec / 60),
    };

    // fuel
    fuelUsage[bumperNumber] = {
      bumper_number: bumperNumber,
      idling_fuel: fuelTotals.idling_fuel,
      loading_fuel: fuelTotals.loading_fuel,
      dumping_fuel: fuelTotals.dumping_fuel,
      moving_loaded_fuel: fuelTotals.moving_loaded_fuel,
      moving_unloaded_fuel: fuelTotals.moving_unloaded_fuel,
    };

    // material and trips
    materialDump[bumperNumber] = {
      bumper_number: bumperNumber,
      total: totalMaterial,
    };

    // finalize trips count
    tripsCount[bumperNumber] = {
      bumper_number: bumperNumber,
      total: totalTrips,
    };
  });

  return {
    planData,
    travel_time: travelTimeInMins,
    fuel_usage: fuelUsage,
    material_dump: materialDump,
    trips_count: tripsCount,
  };
};
